/* eslint-disable @typescript-eslint/no-shadow */
/* eslint-disable react/no-unknown-property */

import { forwardRef, useEffect } from 'react'
import { useInView } from 'react-intersection-observer'
import { isEmpty, isNonNullish } from 'remeda'
import { ApparelProduct } from '@components/ApparelProduct/ApparelProduct'
import type {
  CollectionBannerProps,
  CollectionMessageData,
  Product,
} from '@velasca/types'
import { CollectionBanner } from '@velasca/ui/collection-banner'
import { CollectionTextSection } from '@velasca/ui/collection-text-section'
import * as S from './styles'
import type { ApparelCollectionCommonProps } from './types'

interface ApparelCollectionPresentationalProps
  extends ApparelCollectionCommonProps {
  banner?: CollectionBannerProps
  products?: Product[] | null
  canLoadMore?: boolean
  onLoadMore: () => void
  loading?: boolean
  collectionTopMessageData?: CollectionMessageData
  showCollectionBannerOnMobile?: boolean
  productsCount: number
}

export const ApparelCollectionPresentational = forwardRef<
  HTMLDivElement,
  ApparelCollectionPresentationalProps
>(
  (
    {
      showBanner = false,
      showSingleVariant = false,
      loading = false,
      skeletonLength = 6,
      productsCount,
      products = [],
      banner,
      showProductType,
      showReviewBadge,
      className = '',
      canLoadMore,
      onLoadMore,
      shouldLoadAllProducts,
      collectionTopMessageData,
      areCollectionCardImagesWornApparel,
      showCollectionBannerOnMobile,
    }: ApparelCollectionPresentationalProps,
    ref,
  ) => {
    const [loadMoreRef, inView] = useInView({
      rootMargin: `100000px 0px 1000px 0px`,
      triggerOnce: true,
    })

    useEffect(() => {
      if (!shouldLoadAllProducts && inView && canLoadMore && !loading) {
        onLoadMore()
      }
    }, [inView, canLoadMore, shouldLoadAllProducts, loading, onLoadMore])

    if (isNonNullish(products) && isEmpty(products) && !loading) {
      return null
    }

    return (
      <div className={className}>
        {collectionTopMessageData && !isEmpty(collectionTopMessageData) && (
          <CollectionTextSection
            title={collectionTopMessageData.title}
            upperTitle={collectionTopMessageData.upperTitle}
            paragraph={collectionTopMessageData.paragraph}
          />
        )}
        <S.Grid
          ref={ref}
          fixedHeight={loading || (!shouldLoadAllProducts && canLoadMore)}
          totalProductsLength={productsCount}
        >
          {showBanner && (loading || banner) && (
            <div
              css={`
                /* Hides CollectionBanner component on mobile */
                display: ${showCollectionBannerOnMobile ? 'block' : 'none'};

                @media screen and (min-width: 768px) {
                  display: block;
                  align-self: flex-start;
                }
              `}
            >
              {loading && isEmpty(products ?? []) ? (
                <CollectionBanner loading />
              ) : (
                <CollectionBanner {...banner} />
              )}
            </div>
          )}
          {products?.map((product, i, products) => {
            // If there is a load more, in order to NOT show last product that could have the wrong numbers of Variant Dots
            return (
              (shouldLoadAllProducts ||
                !canLoadMore ||
                products.length !== i + 1) && (
                <S.CardContent key={`${product?.title}-${i}`}>
                  <ApparelProduct
                    showSeason
                    showSingleVariant={showSingleVariant}
                    showProductType={showProductType}
                    showReviewBadge={showReviewBadge}
                    showBrandingOnWornImage={areCollectionCardImagesWornApparel}
                    {...product}
                  />
                </S.CardContent>
              )
            )
          })}
          {/* Show some loading cards when:
          - I am loading in infinite scrolling mode and I can load some more
          - When I am loading all products at the same time and the status is loading.

          I have the number of products from liquid, so I can load every single skeleton. The -1 is because I am hiding a loaded product. So I need to put a skeleton more.
           */}
          {((!shouldLoadAllProducts && canLoadMore) || loading) &&
            [...Array(skeletonLength).keys()].map((id) => (
              <S.CardContent
                key={id}
                {...(id === 0 && products && !isEmpty(products)
                  ? { ref: loadMoreRef }
                  : {})}
              >
                <ApparelProduct
                  showSeason
                  loading
                  showSingleVariant={showSingleVariant}
                  showProductType={showProductType}
                  showReviewBadge={showReviewBadge}
                />
              </S.CardContent>
            ))}
        </S.Grid>
      </div>
    )
  },
)
